import { Button } from "@/components/ui/button";
import {
  ZoomIn,
  ZoomOut,
  Maximize,
  Minimize,
  BookOpen,
  FileText,
  Bookmark,
  Highlighter,
  Moon,
  Sun,
  Menu,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { useTheme } from "next-themes";
import { useIsMobile } from "@/hooks/use-mobile";

interface PDFToolbarProps {
  visible: boolean;
  currentPage: number;
  totalPages: number;
  zoomPercent: number;
  displayMode: "single" | "double";
  isFullscreen: boolean;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onPageChange: (page: number) => void;
  onDisplayModeToggle: () => void;
  onFullscreenToggle: () => void;
  onThumbnailToggle: () => void;
  onAnnotationToggle: () => void;
  onBookmarkToggle: () => void;
  className?: string;
}

export const PDFToolbar = ({
  visible,
  currentPage,
  totalPages,
  zoomPercent,
  displayMode,
  isFullscreen,
  onZoomIn,
  onZoomOut,
  onPageChange,
  onDisplayModeToggle,
  onFullscreenToggle,
  onThumbnailToggle,
  onAnnotationToggle,
  onBookmarkToggle,
  className = "",
}: PDFToolbarProps) => {
  const [pageInput, setPageInput] = useState(currentPage.toString());
  const { theme, setTheme } = useTheme();
  const isMobile = useIsMobile("phone"); // 這裡只會用到手機版差異

  const handlePageInputChange = (value: string) => {
    setPageInput(value);
    const pageNum = parseInt(value);
    if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages) {
      onPageChange(pageNum);
    }
  };

  return (
    <div
      className={`absolute left-0 right-0 top-0 z-40 border-b border-[hsl(var(--toolbar-border))] bg-[hsl(var(--toolbar-bg))]/95 backdrop-blur-sm transition-transform duration-300 ${
        visible ? "translate-y-0" : "-translate-y-full"
      }`}
    >
      <div className="flex items-center justify-between gap-2 p-2">
        {/* Left Section */}
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9"
            title="縮圖"
            onClick={onThumbnailToggle}
          >
            <Menu className="h-4 w-4" />
          </Button>
        </div>

        {/* Center Section */}
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={onZoomOut}
            className="h-9 w-9"
          >
            <ZoomOut className="h-4 w-4" />
          </Button>

          <span className="min-w-16 text-center text-sm text-muted-foreground">
            {zoomPercent}%
          </span>

          <Button
            variant="ghost"
            size="icon"
            onClick={onZoomIn}
            className="h-9 w-9"
          >
            <ZoomIn className="h-4 w-4" />
          </Button>

          <div className="h-6 w-px bg-border" />

          <Button
            variant="ghost"
            size="icon"
            onClick={onDisplayModeToggle}
            className="h-9 w-9"
            title={displayMode === "single" ? "雙頁" : "單頁"}
          >
            {displayMode === "single" ? (
              <FileText className="h-4 w-4" />
            ) : (
              <BookOpen className="h-4 w-4" />
            )}
          </Button>

          {/* 行動裝置模式時不顯示   */}
          {!isMobile ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={onFullscreenToggle}
              className="h-9 w-9"
            >
              {isFullscreen ? (
                <Minimize className="h-4 w-4" />
              ) : (
                <Maximize className="h-4 w-4" />
              )}
            </Button>
          ) : null}
        </div>

        {/* Right Section */}
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9"
            title="標註"
            onClick={onAnnotationToggle}
          >
            <Highlighter className="h-4 w-4" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9"
            title="書籤"
            onClick={onBookmarkToggle}
          >
            <Bookmark className="h-4 w-4" />
          </Button>

          <div className="h-6 w-px bg-border" />

          <Button
            variant="ghost"
            size="icon"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            className="h-9 w-9"
          >
            {theme === "dark" ? (
              <Sun className="h-4 w-4" />
            ) : (
              <Moon className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
